import { useEffect } from 'react';
import { useFocusTrap } from './hooks/useFocusTrap';

interface EDCheckinIntroModalProps {
  onDismiss: () => void;
  onClose?: () => void;
}

export default function EDCheckinIntroModal({ onDismiss, onClose }: EDCheckinIntroModalProps) {
  const focusTrapRef = useFocusTrap(true);

  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape' && onClose) onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      ref={focusTrapRef}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.92)',
        zIndex: 9600,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        animation: 'edIntroFadeIn 0.35s ease',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="ED check-in intro"
    >
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          background: 'var(--color-bg-card, #1a2035)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid color-mix(in srgb, var(--color-danger) 35%, transparent)',
          boxShadow: 'var(--shadow-overlay)',
          overflow: 'hidden',
          animation: 'edIntroPop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          position: 'relative',
        }}
      >
        {onClose && (
          // eslint-disable-next-line cw/no-inline-button-style -- EDCheckinIntro modal close × — 44×44 round icon button, not a chamfered .btn variant
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 10,
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: 'none',
              background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)',
              color: '#fff',
              fontSize: 'var(--fs-md)',
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >×</button>
        )}
        <div
          style={{
            background: 'var(--gradient-crisis)',
            padding: '20px 20px 16px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 'var(--fs-4xl)', marginBottom: 8, lineHeight: 1 }}>🚨</div>
          <h2 style={{ fontSize: 'var(--fs-md-plus)', fontWeight: 800, color: '#fff', margin: '0 0 4px' }}>
            Crisis Check-in
          </h2>
          <p style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.8)', margin: 0 }}>
            Your real-time documentation tool
          </p>
        </div>

        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 'var(--fs-lg)', flexShrink: 0 }}>🕐</span>
              <div>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-text-primary, #e2e8f0)', marginBottom: 2 }}>Timestamped logs</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>Every check-in is automatically timestamped — building a real record of your visit that can't be dismissed later.</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 'var(--fs-lg)', flexShrink: 0 }}>⏱️</span>
              <div>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-text-primary, #e2e8f0)', marginBottom: 2 }}>Every 30–60 minutes</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>Log regularly so your pain level and symptoms are documented throughout your visit, not just at arrival.</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 'var(--fs-lg)', flexShrink: 0 }}>📄</span>
              <div>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-text-primary, #e2e8f0)', marginBottom: 2 }}>Goes into your ED report</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>All check-ins are exported in your ED Report — a PDF you can show staff or keep as advocacy documentation.</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 'var(--fs-lg)', flexShrink: 0 }}>🛡️</span>
              <div>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--color-text-primary, #e2e8f0)', marginBottom: 2 }}>You are believed here</div>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>Flag safety concerns if staff dismiss your pain or treat you unfairly. Your experience matters and it's worth recording.</div>
              </div>
            </div>
          </div>

          {/* eslint-disable-next-line cw/no-inline-button-style -- EDCheckinIntro Got-it CTA — crisis-tone onboarding-modal gradient (parked family, ED-amber palette) */}
          <button
            onClick={onDismiss}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'var(--gradient-crisis)',
              color: '#fff',
              fontSize: 'var(--fs-sm)',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 14px color-mix(in srgb, var(--color-crisis) 35%, transparent)',
            }}
          >
            Got it — start logging 📋
          </button>
        </div>
      </div>

      <style>{`
        @keyframes edIntroFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes edIntroPop {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; }
        }
        html.reduced-motion * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
      `}</style>
    </div>
  );
}
